import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  TextField,
  Typography,
  InputAdornment,
  Button,
  Tooltip,
} from '@mui/material';
import { useFormik } from 'formik';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { array, object, string } from 'yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';
import DialogTransition from './Common/DialogTransition';
import MultiSelect from './Common/MultiSelect';
import REGEX from '../const/Regex';

/**
 * @component AddUserModal
 * @description AddUserModal - A modal component for adding or editing users.
 * @param {Object} props - The props for the component.
 * @param {string} props.mode - The mode of the modal (create or edit).
 * @param {Function} props.setMode - A function to set the mode of the modal.
 * @param {Object} props.currentUserDetail - Details of the current user.
 * @param {boolean} props.open - Indicates whether the modal is open or not.
 * @param {Function} props.setOpen - A function to toggle the visibility of the modal.
 * @param {Function} props.createUser - A function to create a new user.
 * @param {Function} props.updateUser - A function to update an existing user.
 * @param {Function} props.fetchUserDetails - A function to fetch user details.
 * @param {Function} props.getGroupList - A function to get the list of groups.
 * @param {string} props.organizationID - The ID of the organization.
 * @returns {JSX.Element} AddUserModal component
 */

const AddUserModal = ({
  mode,
  setMode,
  currentUserDetail,
  open,
  setOpen,
  createUser,
  updateUser,
  getGroupList,
  organizationID,
  fetchUserDetails,
  pageIndex,
  pageSize,
  searchTerm,
  passwordPolicy,
}) => {
  const [hidden, setHidden] = useState(true);
  const [groupList, setGroupList] = useState([]);
  const [isActive, setIsActive] = useState(true);

  const initialValues = {
    username: '',
    password: '',
    email: '',
    lastName: '',
    firstName: '',
    groups: [], // Initialize groups as an empty array
  };

  const createPasswordRules = (policy) => {
    let rules = string().required('Required');

    if (policy?.password_min_length > 0) {
      rules = rules.min(
        policy.password_min_length,
        `Password must be at least ${policy.password_min_length} characters long`
      );
    }

    if (policy?.password_max_length > 0) {
      rules = rules.max(
        policy.password_max_length,
        `Password must be at most ${policy.password_max_length} characters long`
      );
    }

    if (policy?.password_min_special_char > 0) {
      const regex = new RegExp(
        `(?:[^A-Za-z0-9].*?){${policy.password_min_special_char},}`
      );
      rules = rules.matches(
        regex,
        `Password must contain at least ${policy.password_min_special_char} special character(s)`
      );
    }

    if (policy?.password_min_uppercase > 0) {
      const regex = new RegExp(
        `(?:[A-Z].*?){${policy.password_min_uppercase},}`
      );
      rules = rules.matches(
        regex,
        `Password must contain at least ${policy.password_min_uppercase} uppercase letter(s)`
      );
    }

    if (policy?.password_min_lowercase > 0) {
      const regex = new RegExp(
        `(?:[a-z].*?){${policy.password_min_lowercase},}`
      );
      rules = rules.matches(
        regex,
        `Password must contain at least ${policy.password_min_lowercase} lowercase letter(s)`
      );
    }

    return rules;
  };

  // Generate password validation rules based on the policy
  const passwordRules = createPasswordRules(passwordPolicy);

  /**
   * @constant {Object} validationSchema
   * @description This is the validation schema for a user form. It defines the validation rules for the form fields using Yup.
   * @property {Object} username - The username field, which is required.
   * @property {Object} password - The password field, which is conditionally required based on the value of the "mode" field.
   * @property {Object} email - The email field, which is required and must be a valid email address.
   * @property {Object} isEmailVerified - A boolean field indicating if the email is verified, which is required.
   * @property {Object} lastName - The last name field, which is required.
   * @property {Object} firstName - The first name field, which is required.
   * @property {Object} groups - An array of group objects. Each group object must have a "label" and a "value", both of which are required.
   * @property {Object[]} groups.label - The label of the group, which is required.
   * @property {Object[]} groups.value - The value of the group, which is required.
   */

  const validationSchema = object({
    username: string()
      .max(320, 'Username must be at most 320 characters')
      .min(1, 'Username must be at least 1 character')
      .matches(REGEX.USERNAME, 'Username is not valid')
      .required('Required'),
    email: string()
      .max(254, 'Email must be at most 254 characters')
      .email('Email address is not valid')
      .matches(REGEX.EMAIL, 'Email address is not valid')
      .required('Required'),
    lastName: string()
      .min(1, 'Last name must be at least 1 character')
      .max(30, 'Last name must be at most 30 characters')
      .required('Required'),
    firstName: string()
      .min(1, 'First name must be at least 1 character')
      .max(30, 'First name must be at most 30 characters')
      .required('Required'),
    password: mode === 'edit' ? '' : passwordRules,
    groups: array()
      .nullable()
      .min(0, 'Please select at least one group')
      .of(
        object().shape({
          label: string().required('Required'),
          value: string().required('Required'),
        })
      )
      .optional('Please select at least one group'),
  });

  /**
   * @constant {Object} formikForm
   * @description Configuration for Formik, handling form state, validation, and submission for user management.
   * @property {Object} initialValues - The initial values of the form fields.
   * @property {Object} validationSchema - The validation schema for the form fields.
   * @property {function} onSubmit - The function to handle form submission.
   * @param {Object} initialValues - Initial values for the form fields.
   * @param {string} initialValues.username - The initial username value.
   * @param {string} initialValues.password - The initial password value.
   * @param {string} initialValues.email - The initial email value.
   * @param {boolean} initialValues.isEmailVerified - The initial email verification status.
   * @param {string} initialValues.firstName - The initial first name value.
   * @param {string} initialValues.lastName - The initial last name value.
   * @param {Array} initialValues.groups - The initial groups value.
   * @param {Object} validationSchema - The Yup validation schema for the form.
   * @param {function} onSubmit - Function called when the form is submitted.
   * @param {Object} values - The current form values.
   * @param {string} values.username - The username value from the form.
   * @param {string} values.password - The password value from the form.
   * @param {string} values.email - The email value from the form.
   * @param {boolean} values.isEmailVerified - The email verification status from the form.
   * @param {string} values.firstName - The first name value from the form.
   * @param {string} values.lastName - The last name value from the form.
   * @param {Array} values.groups - The groups value from the form.
   * @returns {Object} formikForm - The Formik configuration object.
   */

  const formikForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      // Handle form submission logic here
      const payload = {
        organization_id: organizationID,
        username: values.username,
        enabled: isActive,
        email: values.email,
        given_name: values.firstName,
        family_name: values.lastName,
        password: values.password,
        group_ids: values.groups?.length
          ? values.groups?.map((item) => {
              return item?.value;
            })
          : [],
      };
      if (mode === 'edit') {
        const updatePayload = Object.fromEntries(
          Object.entries(payload).filter(
            ([key]) => !['organization_id', 'password'].includes(key)
          )
        );
        updateUser(organizationID, currentUserDetail?.id, updatePayload).then(
          () => {
            setMode('create');
            fetchUserDetails({
              pageIndex,
              pageSize,
              searchTerm,
              organizationID,
            });
          }
        );
      } else {
        createUser(payload, organizationID).then(() => {
          setMode('create');
          fetchUserDetails({ pageIndex, pageSize, searchTerm, organizationID });
        });
      }
      setOpen(!open); // Close the dialog after successful submission (optional)
      formikForm?.handleReset();
    },
  });

  useEffect(() => {
    if (mode === 'edit') {
      formikForm.setValues({
        email: currentUserDetail?.email,
        lastName: currentUserDetail?.family_name,
        firstName: currentUserDetail?.given_name,
        username: currentUserDetail?.username,
        groups: currentUserDetail?.group_detail?.map((item) => {
          return {
            label: `${item?.name} (Group : ${item?.product_name})`,
            value: item?.id,
          };
        }),
      });
      setIsActive(currentUserDetail?.enabled);
    }
  }, [mode]);

  const handleChange = (e) => {
    formikForm.handleChange(e);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const changeHidden = () => {
    setHidden(!hidden);
  };

  useEffect(() => {
    if (organizationID) {
      getGroupList(organizationID).then((res) => {
        setGroupList(res?.data);
      });
    }
  }, [organizationID]);

  const formattedData = groupList?.map((item) => {
    return {
      label: `${item?.name} (Group : ${item?.product_name})`,
      value: item?.id,
    };
  });

  const handleOnClose = useCallback(() => {
    setMode('create');
    setOpen((prev) => !prev);
    formikForm.handleReset();
  }, [setMode, setOpen, formikForm]);

  return (
    <Dialog
      maxWidth="xs"
      onClose={handleOnClose}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>{mode === 'edit' ? 'Edit User' : 'Create User'}</DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        sx={{ position: 'absolute', right: 16, top: 8 }}
        onClick={handleOnClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <TextField
              required
              disabled={mode === 'edit'}
              label="Username"
              fullWidth
              placeholder="Enter username"
              name="username"
              value={formikForm?.values?.username}
              onChange={handleChange}
              aria-label="username"
              onBlur={formikForm?.handleBlur}
              helperText={
                formikForm?.errors?.username && formikForm?.touched?.username
                  ? formikForm?.errors?.username
                  : null
              }
              error={
                Boolean(formikForm?.errors?.username) &&
                formikForm?.touched?.username
              }
              InputProps={{
                endAdornment: (
                  <Tooltip
                    ml={2}
                    title={
                      <Typography>
                        - Your username can be any combination of numbers and
                        alphabets and special characters. <br />- Your username
                        can contain uppercase characters. <br />
                        - Your username can be an email address. <br />
                        - Only special characters are not allowed. <br />-
                        Minimum 2 characters are required in username. <br />-
                        Maximum 320 characters are allowed in username. <br />
                      </Typography>
                    }
                    arrow
                    placement="top"
                    sx={{ marginLeft: 1 }}
                  >
                    <InfoOutlinedIcon />
                  </Tooltip>
                ),
              }}
            />
          </Grid>
          {mode === 'create' ? (
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Password"
                placeholder="Enter password"
                name="password"
                value={formikForm?.values?.password}
                onChange={handleChange}
                aria-label="password"
                type={!hidden ? 'text' : 'password'}
                autoComplete="off"
                onBlur={formikForm?.handleBlur}
                helperText={
                  formikForm?.errors?.password && formikForm?.touched?.password
                    ? formikForm?.errors?.password
                    : null
                }
                error={
                  Boolean(formikForm?.errors?.password) &&
                  formikForm?.touched?.password
                }
                InputProps={{
                  endAdornment: (
                    <>
                      <InputAdornment>
                        <IconButton
                          color="primary"
                          aria-label="toggle password visibility"
                          onClick={changeHidden}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {!hidden ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                      <Tooltip
                        ml={2}
                        title={
                          <Typography>
                            - Your password must contain at least{' '}
                            {passwordPolicy?.password_min_length} characters and
                            at most {passwordPolicy?.password_max_length}{' '}
                            characters. <br />- Your password must contain at
                            least {passwordPolicy?.password_min_special_char}{' '}
                            special character. <br />- Your password must
                            contain at least{' '}
                            {passwordPolicy?.password_min_uppercase} upper case
                            character. <br />- Your password must contain at
                            least {passwordPolicy?.password_min_lowercase} lower
                            case character.{' '}
                          </Typography>
                        }
                        arrow
                        placement="top"
                        sx={{ marginLeft: 1 }}
                      >
                        <InfoOutlinedIcon />
                      </Tooltip>
                    </>
                  ),
                }}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Email"
              disabled={mode === 'edit'}
              placeholder="Enter valid email"
              name="email"
              value={formikForm?.values?.email}
              onChange={handleChange}
              aria-label="email"
              onBlur={formikForm?.handleBlur}
              helperText={
                formikForm?.errors?.email && formikForm?.touched?.email
                  ? formikForm?.errors?.email
                  : null
              }
              error={
                Boolean(formikForm?.errors?.email) && formikForm?.touched?.email
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="First name"
              placeholder="Enter first name"
              name="firstName"
              value={formikForm?.values?.firstName}
              onChange={handleChange}
              aria-label="firstName"
              onBlur={formikForm?.handleBlur}
              helperText={
                formikForm?.errors?.firstName && formikForm?.touched?.firstName
                  ? formikForm?.errors?.firstName
                  : null
              }
              error={
                Boolean(formikForm?.errors?.firstName) &&
                formikForm?.touched?.firstName
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label="Last name"
              placeholder="Enter last name"
              name="lastName"
              value={formikForm?.values?.lastName}
              onChange={handleChange}
              aria-label="lastName"
              onBlur={formikForm?.handleBlur}
              helperText={
                formikForm?.errors?.lastName && formikForm?.touched?.lastName
                  ? formikForm?.errors?.lastName
                  : null
              }
              error={
                Boolean(formikForm?.errors?.lastName) &&
                formikForm?.touched?.lastName
              }
            />
          </Grid>
          <Grid item xs={12}>
            <MultiSelect
              multiple
              fullWidth
              label="Groups"
              placeholder="Select groups"
              name="groups"
              value={formikForm?.values?.groups}
              onChange={(e) => formikForm.setFieldValue('groups', e)}
              noOptionsText="No groups available."
              limitTags={3}
              aria-label="groups"
              onBlur={formikForm?.handleBlur}
              helperText={
                formikForm?.errors?.groups && formikForm?.touched?.groups
                  ? formikForm?.errors?.groups
                  : null
              }
              error={
                Boolean(formikForm?.errors?.groups) &&
                formikForm?.touched?.groups
              }
              options={formattedData}
              getOptionLabel={(option) => {
                return option?.label ? option?.label : '';
              }}
              isOptionEqualToValue={(option, val) =>
                option?.value === val?.value
              }
              renderOption={(props, option) => (
                <Box
                  display="flex"
                  flexDirection="row"
                  flexGrow={1}
                  alignItems="center"
                  {...props}
                  minHeight="unset !important"
                  height="unset !important"
                  key={option?.id}
                >
                  <Box display="flex" flexGrow={1} flexDirection="column">
                    <Typography variant="body2">{`${option?.label}`}</Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          title="Reset"
          variant="outlined"
          onClick={() => {
            formikForm.handleReset();
            if (mode === 'edit') {
              formikForm.setFieldValue('username', currentUserDetail?.username);
              formikForm.setFieldValue('email', currentUserDetail?.email);
            }
          }}
        >
          Reset
        </Button>

        <Button variant="contained" onClick={formikForm.handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddUserModal.propTypes = {
  mode: PropTypes.string.isRequired,
  setMode: PropTypes.func.isRequired,
  currentUserDetail: PropTypes.oneOfType([PropTypes.object]).isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  createUser: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  fetchUserDetails: PropTypes.func.isRequired,
  getGroupList: PropTypes.func.isRequired,
  organizationID: PropTypes.number.isRequired,
  pageIndex: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  searchTerm: PropTypes.string.isRequired,
  emailAsUsername: PropTypes.bool.isRequired,
  passwordPolicy: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default AddUserModal;
